import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '../components/ui/dialog';
import {
  Wallet,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Loader2,
  RefreshCw,
  ArrowUpRight,
  ArrowDownRight,
  ExternalLink,
  Copy,
  Check,
  Plus,
  Minus,
  Clock,
  CheckCircle,
  XCircle,
} from 'lucide-react';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatUSD = (value) => {
  if (typeof value === 'string') return value;
  return `$${value?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('es-ES', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export const Treasury = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [balance, setBalance] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [depositAddress, setDepositAddress] = useState(null);
  const [copied, setCopied] = useState(false);
  
  // Modals
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  
  // Form states
  const [depositTxHash, setDepositTxHash] = useState('');
  const [depositAmount, setDepositAmount] = useState('');
  const [depositFromAddress, setDepositFromAddress] = useState('');
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawAddress, setWithdrawAddress] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchData = useCallback(async () => {
    if (!user?.id) return;
    
    try {
      const [balanceRes, txRes, addrRes] = await Promise.all([
        fetch(`${API_URL}/api/treasury/balance/${user.id}`).catch(() => null),
        fetch(`${API_URL}/api/treasury/transactions/${user.id}`).catch(() => null),
        fetch(`${API_URL}/api/treasury/deposit-address`).catch(() => null),
      ]);

      if (balanceRes?.ok) {
        const data = await balanceRes.json();
        setBalance(data.balance);
      }
      if (txRes?.ok) {
        const data = await txRes.json();
        setTransactions(data.transactions || []);
      }
      if (addrRes?.ok) {
        const data = await addrRes.json();
        setDepositAddress(data);
      }
    } catch (error) {
      console.error('Fetch error:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  const copyAddress = () => {
    if (depositAddress?.deposit_address) {
      navigator.clipboard.writeText(depositAddress.deposit_address);
      setCopied(true);
      toast.success('Dirección copiada');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleSubmitDeposit = async () => {
    if (!depositTxHash || !depositAmount || !depositFromAddress) {
      toast.error('Completa todos los campos');
      return;
    }
    
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/treasury/deposit/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: user.id,
          tx_hash: depositTxHash,
          amount: parseFloat(depositAmount),
          from_address: depositFromAddress,
        })
      });
      
      const data = await res.json();
      
      if (res.ok) {
        toast.success('Depósito enviado para verificación');
        setShowDepositModal(false);
        setDepositTxHash('');
        setDepositAmount('');
        setDepositFromAddress('');
        fetchData();
      } else {
        toast.error(data.detail || 'Error al verificar depósito');
      }
    } catch (e) {
      toast.error('Error de conexión');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitWithdraw = async () => {
    if (!withdrawAmount || !withdrawAddress) {
      toast.error('Completa todos los campos');
      return;
    }
    
    if (parseFloat(withdrawAmount) > (balance?.available_balance || 0)) {
      toast.error('Saldo insuficiente');
      return;
    }
    
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/treasury/withdraw/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: user.id,
          amount: parseFloat(withdrawAmount),
          to_address: withdrawAddress,
        })
      });
      
      const data = await res.json();
      
      if (res.ok) {
        toast.success('Solicitud de retiro enviada');
        setShowWithdrawModal(false);
        setWithdrawAmount('');
        setWithdrawAddress('');
        fetchData();
      } else {
        toast.error(data.detail || 'Error al solicitar retiro');
      }
    } catch (e) {
      toast.error('Error de conexión');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-8 max-w-5xl mx-auto" data-testid="treasury-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Wallet className="w-8 h-8 text-amber-400" />
            Mi Capital
          </h1>
          <p className="text-muted-foreground mt-1">
            Gestiona tus depósitos y retiros de USDC
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleRefresh} disabled={refreshing}>
            <RefreshCw className={cn("w-4 h-4 mr-2", refreshing && "animate-spin")} />
            Actualizar
          </Button>
        </div>
      </div>

      {/* Balance Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-green-500/10 to-transparent border-green-500/20">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-3">
              <DollarSign className="w-8 h-8 text-green-400" />
              <Badge variant="outline" className="text-xs text-green-400">Disponible</Badge>
            </div>
            <p className="text-3xl font-bold text-green-400">
              {formatUSD(balance?.available_balance || 0)}
            </p>
            <p className="text-xs text-muted-foreground mt-1">Balance disponible</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-3">
              <Clock className="w-8 h-8 text-yellow-400" />
              <Badge variant="outline" className="text-xs">Pendiente</Badge>
            </div>
            <p className="text-3xl font-bold text-yellow-400">
              {formatUSD((balance?.pending_deposits || 0) + (balance?.pending_withdrawals || 0))}
            </p>
            <p className="text-xs text-muted-foreground mt-1">En proceso</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-3">
              <ArrowDownRight className="w-8 h-8 text-blue-400" />
              <Badge variant="outline" className="text-xs">Depositado</Badge>
            </div>
            <p className="text-3xl font-bold">{formatUSD(balance?.total_deposited || 0)}</p>
            <p className="text-xs text-muted-foreground mt-1">Total depositado</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-3">
              <ArrowUpRight className="w-8 h-8 text-amber-400" />
              <Badge variant="outline" className="text-xs">Retirado</Badge>
            </div>
            <p className="text-3xl font-bold">{formatUSD(balance?.total_withdrawn || 0)}</p>
            <p className="text-xs text-muted-foreground mt-1">Total retirado</p>
          </CardContent>
        </Card>
      </div>

      {/* Action Buttons */}
      <div className="grid md:grid-cols-2 gap-4">
        <Card className="bg-gradient-to-br from-green-500/5 to-transparent border-green-500/20 cursor-pointer hover:border-green-500/40 transition-colors"
              onClick={() => setShowDepositModal(true)}>
          <CardContent className="p-6 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center">
                <Plus className="w-6 h-6 text-green-400" />
              </div>
              <div>
                <h3 className="font-semibold">Depositar USDC</h3>
                <p className="text-sm text-muted-foreground">Añade fondos a tu cuenta</p>
              </div>
            </div>
            <Badge className="bg-green-500/20 text-green-400">Polygon</Badge>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-500/5 to-transparent border-amber-500/20 cursor-pointer hover:border-amber-500/40 transition-colors"
              onClick={() => setShowWithdrawModal(true)}>
          <CardContent className="p-6 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center">
                <Minus className="w-6 h-6 text-amber-400" />
              </div>
              <div>
                <h3 className="font-semibold">Retirar USDC</h3>
                <p className="text-sm text-muted-foreground">Retira a tu wallet</p>
              </div>
            </div>
            <Badge className="bg-amber-500/20 text-amber-400">Min $10</Badge>
          </CardContent>
        </Card>
      </div>

      {/* Deposit Address */}
      {depositAddress && (
        <Card className="bg-gradient-to-br from-blue-500/5 to-transparent border-blue-500/20">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Wallet className="w-5 h-5 text-blue-400" />
              Dirección de Depósito
            </CardTitle>
            <CardDescription>
              Envía USDC en red Polygon a esta dirección
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-2">
              <div className="flex-1 bg-card/50 border border-border rounded-lg px-4 py-3 font-mono text-sm truncate">
                {depositAddress.deposit_address}
              </div>
              <Button onClick={copyAddress} variant="outline" className="shrink-0">
                {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <Badge className="bg-purple-500/20 text-purple-400">Polygon</Badge>
              <Badge className="bg-blue-500/20 text-blue-400">USDC</Badge>
              <span>Mínimo: ${depositAddress.min_deposit}</span>
            </div>
            <p className="text-xs text-yellow-500">
              ⚠️ Solo envía USDC en red Polygon. Otros tokens o redes resultarán en pérdida de fondos.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Transaction History */}
      <Card>
        <CardHeader>
          <CardTitle>Historial de Transacciones</CardTitle>
        </CardHeader>
        <CardContent>
          {transactions.length === 0 ? (
            <div className="text-center py-12">
              <Wallet className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-semibold mb-2">Sin transacciones</h3>
              <p className="text-sm text-muted-foreground">
                Tus depósitos y retiros aparecerán aquí
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {transactions.map((tx, i) => (
                <div 
                  key={i}
                  className="flex items-center justify-between p-4 rounded-lg bg-card/50 border border-border"
                >
                  <div className="flex items-center gap-4">
                    <div className={cn(
                      "w-10 h-10 rounded-full flex items-center justify-center",
                      tx.type === 'deposit' ? "bg-green-500/20" : "bg-amber-500/20"
                    )}>
                      {tx.type === 'deposit' ? (
                        <ArrowDownRight className="w-5 h-5 text-green-400" />
                      ) : (
                        <ArrowUpRight className="w-5 h-5 text-amber-400" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium">
                        {tx.type === 'deposit' ? 'Depósito' : 'Retiro'}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {formatDate(tx.created_at)}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={cn(
                      "font-semibold",
                      tx.type === 'deposit' ? "text-green-400" : "text-amber-400"
                    )}>
                      {tx.type === 'deposit' ? '+' : '-'}{formatUSD(tx.amount)}
                    </p>
                    <Badge className={cn(
                      "text-xs",
                      tx.status === 'confirmed' || tx.status === 'completed' 
                        ? "bg-green-500/20 text-green-400"
                        : tx.status === 'rejected' 
                        ? "bg-red-500/20 text-red-400"
                        : "bg-yellow-500/20 text-yellow-400"
                    )}>
                      {tx.status === 'confirmed' || tx.status === 'completed' ? (
                        <><CheckCircle className="w-3 h-3 mr-1" /> Completado</>
                      ) : tx.status === 'rejected' ? (
                        <><XCircle className="w-3 h-3 mr-1" /> Rechazado</>
                      ) : (
                        <><Clock className="w-3 h-3 mr-1" /> Pendiente</>
                      )}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Deposit Modal */}
      <Dialog open={showDepositModal} onOpenChange={setShowDepositModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Verificar Depósito</DialogTitle>
            <DialogDescription>
              Ya enviaste USDC? Ingresa los detalles de la transacción para verificarla.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Hash de Transacción (TX Hash)</Label>
              <Input 
                placeholder="0x..."
                value={depositTxHash}
                onChange={(e) => setDepositTxHash(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Cantidad (USDC)</Label>
              <Input 
                type="number"
                placeholder="100.00"
                value={depositAmount}
                onChange={(e) => setDepositAmount(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Tu Wallet (desde donde enviaste)</Label>
              <Input 
                placeholder="0x..."
                value={depositFromAddress}
                onChange={(e) => setDepositFromAddress(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDepositModal(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSubmitDeposit} disabled={submitting}>
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Verificar Depósito
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Withdraw Modal */}
      <Dialog open={showWithdrawModal} onOpenChange={setShowWithdrawModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Solicitar Retiro</DialogTitle>
            <DialogDescription>
              Retira USDC a tu wallet en Polygon. Procesamiento en 24 horas.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/20">
              <p className="text-sm text-muted-foreground">Balance disponible</p>
              <p className="text-2xl font-bold text-green-400">
                {formatUSD(balance?.available_balance || 0)}
              </p>
            </div>
            <div className="space-y-2">
              <Label>Cantidad a retirar (USDC)</Label>
              <Input 
                type="number"
                placeholder="100.00"
                max={balance?.available_balance || 0}
                value={withdrawAmount}
                onChange={(e) => setWithdrawAmount(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Wallet de destino (Polygon)</Label>
              <Input 
                placeholder="0x..."
                value={withdrawAddress}
                onChange={(e) => setWithdrawAddress(e.target.value)}
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Mínimo: $10 • Red: Polygon • Token: USDC
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowWithdrawModal(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSubmitWithdraw} disabled={submitting}>
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Solicitar Retiro
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Treasury;
